// src/components/WarehousePendingList.js
import React, { useEffect, useState, useMemo } from "react";
import "./PendingWarehouseList.css";
import RefreshIcon from "../assets/icons/RefreshIcon.svg";

export default function WarehousePendingList({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const userId = Number(localStorage.getItem("user_id") || "0");

  const loadPending = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/warehouse/pending");
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to load pending devices.");
      }
      const data = await res.json();
      setItems(data);
    } catch (err) {
      console.error("Error loading pending warehouse devices:", err);
      setError(err.message || "Failed to load pending devices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedItems = useMemo(() => {
    if (!sortConfig.key) return items;
    return [...items].sort((a, b) => {
      const aVal = (a[sortConfig.key] || "").toString().toLowerCase();
      const bVal = (b[sortConfig.key] || "").toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
  }, [items, sortConfig]);

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const handlePrint = () => {
  const rows = sortedItems.map((it) => `
    <tr>
      <td>${it.asset1_name || "-"}</td>
      <td>${it.asset2_name || "-"}</td>
      <td>${it.serial_number || "-"}</td>
      <td>${it.imei_number || "-"}</td>
      <td>${it.from_location_name || it.from_location_id || "-"}</td>
      <td>${it.to_location_name || it.to_location_id || "-"}</td>
    </tr>
  `).join("");

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Pending Warehouse Receive</title>
        <style>
          body { font-family: Roboto, serif; padding: 24px; }
          h2 { margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 14px; }
          th, td { border: 1px solid #ccc; padding: 8px 10px; text-align: left; }
          thead { background-color: #f5f5f5; }
          tr:nth-child(even) { background-color: #fafafa; }
        </style>
      </head>
      <body>
        <h2>Pending Warehouse Receive</h2>
        <table>
          <thead>
            <tr>
              <th>Model</th>
              <th>Colour</th>
              <th>Serial</th>
              <th>IMEI</th>
              <th>From Location</th>
              <th>To Location</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  win.close();
};

  const handleMarkReceived = async (item) => {
    if (!userId) {
      alert("User ID not found in localStorage.");
      return;
    }
    const identifier = item.serial_number || item.imei_number;
    if (!identifier) {
      alert("No serial/IMEI to use as identifier for this item.");
      return;
    }
    setProcessingId(item.deployment_item_id);
    try {
      const res = await fetch(
        `/api/deployments/${item.deployment_id}/warehouse-receive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, identifier }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Warehouse receive failed.");
      setItems((prev) =>
        prev.filter((it) => it.deployment_item_id !== item.deployment_item_id)
      );
    } catch (err) {
      console.error("Error marking as received:", err);
      alert(err.message || "Failed to mark as received.");
    } finally {
      setProcessingId(null);
    }
  };

  return (
  <div className="warehouse-modal-overlay">
    <div className="warehouse-modal">

      <div className="warehouse-modal-header">
        <h2>Pending Warehouse Receive</h2>
        <div className="warehouse-header-actions no-print">
          <button className="warehouse-btn-icon" onClick={handlePrint} title="Print as PDF">
            🖨 Print PDF
          </button>
          <button className="warehouse-btn-icon" onClick={loadPending} title="Refresh">
            <img src={RefreshIcon} alt="Refresh" className="table-icon" />
          </button>
        </div>
        <button className="warehouse-btn-close no-print" onClick={onClose}>✕</button>
      </div>

      <div className="warehouse-modal-body">
        {loading && <p>Loading pending devices…</p>}
        {error && <p className="error-text">{error}</p>}

        {!loading && !error && items.length === 0 && (
          <p>No devices currently pending warehouse receive.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="warehouse-table-wrap">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort("asset1_name")} className="sortable-header" title="Sort by Model">
                    Model{getSortIndicator("asset1_name")}
                  </th>
                  <th onClick={() => handleSort("asset2_name")} className="sortable-header" title="Sort by Colour">
                    Colour{getSortIndicator("asset2_name")}
                  </th>
                  <th>Serial</th>
                  <th>IMEI</th>
                  <th>From Location</th>
                  <th>To Location</th>
                  <th className="no-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((it) => (
                  <tr key={it.deployment_item_id}>
                    <td>{it.asset1_name}</td>
                    <td>{it.asset2_name}</td>
                    <td>{it.serial_number || "-"}</td>
                    <td>{it.imei_number || "-"}</td>
                    <td>{it.from_location_name || it.from_location_id || "-"}</td>
                    <td>{it.to_location_name || it.to_location_id || "-"}</td>
                    <td className="no-print">
                      <button
                        className="warehouse-btn-action"
                        type="button"
                        disabled={processingId === it.deployment_item_id}
                        onClick={() => handleMarkReceived(it)}
                      >
                        {processingId === it.deployment_item_id ? "Processing..." : "Mark Received"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  </div>
)};